import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  CalendarDays,
  Clock3,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

import "../../styles/TransportSessionLocationsCompact.css";

const validDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));

const validTime = (value) =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""));

const normalizedTime = (value) =>
  String(value || "").slice(0, 5);

const isLocked = (point) =>
  ["COMPLETED", "CANCELLED"].includes(
    String(point?.status || "").toUpperCase()
  ) || point?.isFrozen === true;

const scheduleMinutes = (date, hour) => {
  const day = String(date || "").slice(0, 10);
  const time = normalizedTime(hour);

  if (!validDate(day) || !validTime(time)) return null;

  const result = Date.parse(`${day}T${time}:00Z`);

  return Number.isFinite(result) ? result / 60000 : null;
};

export function getMapScheduleConflict(points) {
  let earliestPossible = -Infinity;
  let blockingIndex = -1;

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];

    const mode = String(
      point?.scheduleType || "FIXED"
    ).toUpperCase();

    const date = String(point?.date || "").slice(0, 10);
    const start = normalizedTime(point?.hour);
    const end = normalizedTime(point?.hourEnd);

    if (mode === "FIXED" && !date) continue;

    const earliest = scheduleMinutes(date, start);
    const latest = scheduleMinutes(
      date,
      mode === "WINDOW" ? end : start
    );

    if (
      !["FIXED", "WINDOW"].includes(mode) ||
      earliest === null ||
      latest === null ||
      latest < earliest
    ) {
      return {
        index,
        blockingIndex: Math.max(0, index - 1),
        reason: "INVALID_WINDOW",
      };
    }

    if (earliest > earliestPossible) {
      earliestPossible = earliest;
      blockingIndex = index;
    }

    if (earliestPossible > latest) {
      return {
        index,
        blockingIndex:
          blockingIndex === index
            ? Math.max(0, index - 1)
            : blockingIndex,
        reason: "CHRONOLOGICAL_CONFLICT",
      };
    }
  }

  return null;
}

export function getMapScheduleSignature(points) {
  return [...points]
    .sort((a, b) => Number(a.sequence) - Number(b.sequence))
    .map((point) =>
      [
        point.id,
        point.sequence,
        String(point.status || "").toUpperCase(),
        String(point.date || "").slice(0, 10),
        normalizedTime(point.hour),
        String(point.scheduleType || "").toUpperCase(),
        normalizedTime(point.hourEnd),
      ].join(":")
    )
    .join("|");
}

const formatSchedule = (point) => {
  if (!point?.date) return "Sem data";

  const [year, month, day] =
    String(point.date).slice(0, 10).split("-");

  const date = `${day}/${month}/${year}`;
  const start = normalizedTime(point.hour);
  const end = normalizedTime(point.hourEnd);

  if (!start) return `${date} · Sem hora`;

  return String(point.scheduleType).toUpperCase() === "WINDOW" && end
    ? `${date} · ${start}–${end}`
    : `${date} · ${start}`;
};


const pointName = (point) =>
  point?.location ||
  point?.name ||
  point?.city ||
  `Ponto ${point?.id ?? "—"}`;

const mapCalendarISO = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const mapCalendarDate = (value) => {
  if (!validDate(value)) return null;

  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, month - 1, day, 12);

  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
};

const mapPopupPosition = (event, requestedWidth, requestedHeight) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const width = Math.min(requestedWidth, window.innerWidth - 24);
  const height = Math.min(requestedHeight, window.innerHeight - 24);

  const left = Math.max(
    12,
    Math.min(rect.left, window.innerWidth - width - 12)
  );

  const top =
    rect.bottom + height + 8 <= window.innerHeight - 12
      ? rect.bottom + 8
      : rect.top - height - 8 >= 12
        ? rect.top - height - 8
        : Math.max(12, (window.innerHeight - height) / 2);

  return { top, left };
};

const MapConflictCalendar = ({
  value,
  position,
  onSelect,
  onClose,
}) => {
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const date = mapCalendarDate(value) || new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onClose]);

  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cellCount = Math.ceil((offset + daysInMonth) / 7) * 7;

  const days = Array.from({ length: cellCount }, (_, index) =>
    new Date(year, month, index - offset + 1, 12)
  );

  const todayISO = mapCalendarISO(new Date());

  const monthTitle = new Intl.DateTimeFormat("pt-PT", {
    month: "long",
    year: "numeric",
  }).format(visibleMonth);

  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat("pt-PT", {
      weekday: "short",
    }).format(new Date(2024, 0, index + 1))
  );

  return createPortal(
    <>
      <div
        className="loadevo-session-calendar-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="loadevo-session-calendar"
        role="dialog"
        aria-modal="true"
        aria-label="Selecionar data"
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div className="loadevo-session-calendar-heading">
          <div className="loadevo-session-calendar-heading-title">
            <CalendarDays size={21} aria-hidden="true" />
            <strong>Selecionar data</strong>
          </div>

          <button
            type="button"
            className="loadevo-session-calendar-close"
            onClick={onClose}
            aria-label="Fechar calendário"
          >
            <X size={19} />
          </button>
        </div>

        <div className="loadevo-session-calendar-month">
          <button
            type="button"
            onClick={() =>
              setVisibleMonth(new Date(year, month - 1, 1))
            }
            aria-label="Mês anterior"
          >
            <ChevronLeft size={19} />
          </button>

          <strong>{monthTitle}</strong>

          <button
            type="button"
            onClick={() =>
              setVisibleMonth(new Date(year, month + 1, 1))
            }
            aria-label="Mês seguinte"
          >
            <ChevronRight size={19} />
          </button>
        </div>

        <div className="loadevo-session-calendar-grid">
          {weekdays.map((weekday, index) => (
            <span
              key={`weekday-${index}`}
              className="loadevo-session-calendar-weekday"
            >
              {weekday}
            </span>
          ))}

          {days.map((day) => {
            const iso = mapCalendarISO(day);
            const selected = iso === value;
            const today = iso === todayISO;
            const outside = day.getMonth() !== month;

            return (
              <button
                key={iso}
                type="button"
                className={[
                  "loadevo-session-calendar-day",
                  selected ? "is-selected" : "",
                  today ? "is-today" : "",
                  outside ? "is-outside" : "",
                ].filter(Boolean).join(" ")}
                aria-pressed={selected}
                aria-label={new Intl.DateTimeFormat("pt-PT", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }).format(day)}
                onClick={() => onSelect(iso)}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>

        <div className="loadevo-session-calendar-footer">
          <button type="button" onClick={onClose}>
            Cancelar
          </button>

          <button
            type="button"
            className="is-today-action"
            onClick={() => onSelect(todayISO)}
          >
            Hoje
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

const MapConflictTimePicker = ({
  kind,
  initialType,
  initialStart,
  initialEnd,
  position,
  onClose,
  onApply,
}) => {
  const [mode, setMode] = useState(
    String(initialType).toUpperCase() === "WINDOW"
      ? "WINDOW"
      : "FIXED"
  );

  const [start, setStart] = useState(
    normalizedTime(initialStart) || "09:00"
  );

  const [end, setEnd] = useState(
    normalizedTime(initialEnd) || "16:00"
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onClose]);

  const canApply =
    validTime(start) &&
    (mode === "FIXED" || (validTime(end) && end >= start));

  return createPortal(
    <>
      <div
        className="loadevo-session-time-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="loadevo-session-time-popup"
        style={{
          top: position.top,
          left: position.left,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={
          kind === "delivery"
            ? "Horário de descarga"
            : "Horário de carga"
        }
      >
        <div className="loadevo-session-time-heading">
          <div>
            <CalendarDays size={22} />
            <strong>
              {kind === "delivery"
                ? "Horário de descarga"
                : "Horário de carga"}
            </strong>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={19} />
          </button>
        </div>

        <div className="loadevo-session-time-modes">
          <button
            type="button"
            className={`loadevo-session-time-mode ${
              mode === "FIXED" ? "is-selected" : ""
            }`}
            onClick={() => setMode("FIXED")}
            aria-pressed={mode === "FIXED"}
          >
            <span className="loadevo-session-time-mode-icons">
              <Clock3 size={21} />
              {mode === "FIXED" && <CheckCircle2 size={20} />}
            </span>

            <strong>Hora fixa</strong>
            <small>Hora específica</small>
          </button>

          <button
            type="button"
            className={`loadevo-session-time-mode ${
              mode === "WINDOW" ? "is-selected" : ""
            }`}
            onClick={() => setMode("WINDOW")}
            aria-pressed={mode === "WINDOW"}
          >
            <span className="loadevo-session-time-mode-icons">
              <CalendarDays size={21} />
              {mode === "WINDOW" && <CheckCircle2 size={20} />}
            </span>

            <strong>Intervalo</strong>
            <small>Chegada flexível</small>
          </button>
        </div>

        <div
          className={`loadevo-session-time-fields ${
            mode === "WINDOW" ? "is-window" : ""
          }`}
        >
          <label>
            <span>
              {mode === "WINDOW" ? "Início" : "Hora marcada"}
            </span>
            <input
              type="time"
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>

          {mode === "WINDOW" && (
            <label>
              <span>Fim</span>
              <input
                type="time"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            </label>
          )}
        </div>

        {mode === "WINDOW" &&
          validTime(start) &&
          validTime(end) &&
          end < start && (
            <p className="loadevo-session-time-error">
              O fim do intervalo tem de ser posterior ao início.
            </p>
          )}

        <button
          type="button"
          className="loadevo-session-time-apply"
          disabled={!canApply}
          onClick={() =>
            onApply({
              scheduleType: mode,
              hour: start,
              hourEnd: mode === "WINDOW" ? end : null,
            })
          }
        >
          Aplicar horário
        </button>
      </div>
    </>,
    document.body
  );
};

export default function MapReorderScheduleConflict({
  data,
  saving,
  onCancel,
  onSave,
}) {
  const [points, setPoints] = useState(data.points);
  const [error, setError] = useState("");
  const [calendarPopup, setCalendarPopup] = useState(null);
  const [schedulePopup, setSchedulePopup] = useState(null);

  const initialConflict = getMapScheduleConflict(data.points);

  const [editIndex, setEditIndex] = useState(() =>
    [initialConflict?.blockingIndex, initialConflict?.index]
      .find((index) =>
        index != null && !isLocked(data.points[index])
      ) ?? initialConflict?.index ?? 0
  );

  const conflict = getMapScheduleConflict(points);
  const selectedPoint = points[editIndex];
  const previousStop = points[editIndex - 1];
  const nextStop = points[editIndex + 1];

  const movedIndex = points.findIndex(
    (point) => String(point.id) === String(data.movedId)
  );

  const activeConflict = conflict || initialConflict;

  const candidates = activeConflict
    ? [...new Set([
        activeConflict.blockingIndex,
        activeConflict.index,
      ])]
    : [];

  const updatePoint = (changes) => {
    if (!selectedPoint || isLocked(selectedPoint)) return;

    setPoints((previous) =>
      previous.map((point, index) =>
        index === editIndex
          ? { ...point, ...changes }
          : point
      )
    );

    setError("");
  };


  useEffect(() => {
    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        event.preventDefault();
        onCancel();
      }
    };

    document.addEventListener("keydown", onEscape);

    return () => document.removeEventListener("keydown", onEscape);
  }, [onCancel, saving]);

  const handleApply = () => {
    if (!selectedPoint || isLocked(selectedPoint)) return;

    if (
      !validDate(selectedPoint.date) ||
      !validTime(normalizedTime(selectedPoint.hour)) ||
      (
        String(selectedPoint.scheduleType).toUpperCase() === "WINDOW" &&
        (
          !validTime(normalizedTime(selectedPoint.hourEnd)) ||
          selectedPoint.hourEnd < selectedPoint.hour
        )
      )
    ) {
      setError("Introduz uma data e um horário válidos.");
      return;
    }

    if (conflict) {
      const nextIndex = candidates.find(
        (index) => index !== editIndex && !isLocked(points[index])
      );

      if (nextIndex == null) {
        setError(
          "Ainda existe um conflito. Ajusta o horário selecionado ou reverte o movimento."
        );
        return;
      }

      setEditIndex(nextIndex);
      setError("Resolve agora o conflito restante.");
      return;
    }

    onSave(points);
  };

  const routeContext = [
    { label: "Paragem anterior", index: movedIndex - 1 },
    { label: "Paragem movida", index: movedIndex },
    { label: "Paragem seguinte", index: movedIndex + 1 },
  ];

  return createPortal(
    <div className="loadevo-session-conflict-overlay">
      <div
        className="loadevo-session-conflict-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-schedule-conflict-title"
      >
        <div className="loadevo-session-conflict-heading">
          <AlertTriangle size={27} aria-hidden="true" />

          <div>
            <h3 id="map-schedule-conflict-title">
              Conflito de horários
            </h3>

            <p>
              A nova ordem não é compatível com os horários
              definidos. Ajusta a data ou a hora de uma das
              paragens para resolver o conflito.
            </p>
          </div>
        </div>

        <div className="loadevo-session-route-title">
          Posição proposta
        </div>

        <div className="loadevo-session-route-context">
          {routeContext.map(({ label, index }, position) => {
            const point = points[index];

            return (
              <React.Fragment key={label}>
                {position > 0 && (
                  <div className="loadevo-session-route-then">
                    ↓ &nbsp; Depois
                  </div>
                )}

                <div
                  className={[
                    "loadevo-session-route-stop",
                    position === 1 ? "is-moved" : "",
                    !point ? "is-empty" : "",
                  ].filter(Boolean).join(" ")}
                >
                  <span className="loadevo-session-route-index">
                    {point ? index + 1 : "—"}
                  </span>

                  <div className="loadevo-session-route-stop-main">
                    <small>{label}</small>
                    <strong>
                      {point
                        ? pointName(point)
                        : position === 0
                          ? "Início da rota"
                          : "Fim da rota"}
                    </strong>

                    {point && (
                      <span>
                        {point.type === "PICKUP"
                          ? "Carga"
                          : point.type === "DELIVERY"
                            ? "Descarga"
                            : "Ponto estratégico"}
                        {point.city ? ` · ${point.city}` : ""}
                      </span>
                    )}
                  </div>

                  {point && (
                    <span className="loadevo-session-route-time">
                      {formatSchedule(point)}
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <p className="loadevo-session-conflict-explanation">
          {conflict ? (
            <>
              <strong>{pointName(points[conflict.blockingIndex])}</strong>
              {" ("}
              {formatSchedule(points[conflict.blockingIndex])}
              {") entra em conflito com "}
              <strong>{pointName(points[conflict.index])}</strong>
              {" ("}
              {formatSchedule(points[conflict.index])}
              {")."}
            </>
          ) : (
            <>
              <strong>Horários compatíveis.</strong>
              {" A nova ordem já pode ser guardada."}
            </>
          )}
        </p>

        <div className="loadevo-session-route-title">
          Escolhe a paragem a ajustar
        </div>

        <div className="loadevo-session-conflict-choices">
          {candidates.map((index) => {
            const point = points[index];
            const locked = isLocked(point);

            return (
              <button
                key={point.id}
                type="button"
                disabled={locked || saving}
                className={[
                  "loadevo-session-conflict-choice",
                  editIndex === index ? "is-selected" : "",
                ].filter(Boolean).join(" ")}
                aria-pressed={editIndex === index}
                onClick={() => {
                  setEditIndex(index);
                  setError("");
                }}
              >
                <span className="loadevo-session-conflict-point-number">
                  #{index + 1}
                </span>

                <strong>{pointName(point)}</strong>

                <small>
                  {point.type === "PICKUP" ? "Carga" : "Descarga"}
                </small>

                <span className="loadevo-session-conflict-current-time">
                  {formatSchedule(point)}
                </span>

                <span className="loadevo-session-conflict-choice-hint">
                  {locked
                    ? "Bloqueado"
                    : editIndex === index
                      ? "Em edição"
                      : "Selecionar"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="loadevo-session-conflict-editor">
          <div className="loadevo-session-conflict-editor-title">
            <strong>Ajustar data e horário</strong>
          </div>

          <strong>
            A editar: {pointName(selectedPoint)}
          </strong>

          <div className="loadevo-session-conflict-fields">
            <div className="loadevo-session-conflict-date-field">
              <label htmlFor="map-conflict-date">
                Data
              </label>

              <div className="loadevo-session-conflict-date-picker">
                <button
                  id="map-conflict-date"
                  type="button"
                  className="loadevo-session-date-trigger"
                  aria-haspopup="dialog"
                  aria-expanded={Boolean(calendarPopup)}
                  disabled={saving || isLocked(selectedPoint)}
                  onClick={(event) => {
                    setSchedulePopup(null);
                    setCalendarPopup({
                      ...mapPopupPosition(event, 350, 415),
                      pointId: selectedPoint.id,
                    });
                  }}
                >
                  <span className="loadevo-session-date-value">
                    {selectedPoint?.date
                      ? formatSchedule({
                          date: selectedPoint.date,
                          hour: null,
                        }).split(" · ")[0]
                      : "Selecionar data"}
                  </span>

                  <span className="loadevo-session-date-icon">
                    <CalendarDays
                      size={20}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </span>
                </button>
              </div>
            </div>

            <div className="loadevo-session-conflict-schedule-field">
              <label>Horário</label>

              <button
                type="button"
                className="loadevo-session-conflict-schedule-launch"
                aria-haspopup="dialog"
                aria-expanded={Boolean(schedulePopup)}
                disabled={saving || isLocked(selectedPoint)}
                onClick={(event) => {
                  setCalendarPopup(null);
                  setSchedulePopup({
                    ...mapPopupPosition(event, 490, 440),
                    pointId: selectedPoint.id,
                  });
                }}
              >
                <span className="loadevo-session-conflict-schedule-value">
                  {String(selectedPoint?.scheduleType).toUpperCase() ===
                  "WINDOW"
                    ? `${normalizedTime(selectedPoint?.hour) || "--:--"}–${
                        normalizedTime(selectedPoint?.hourEnd) || "--:--"
                      }`
                    : normalizedTime(selectedPoint?.hour) ||
                      "Definir hora"}
                </span>

                <span className="loadevo-session-conflict-schedule-caption">
                  {String(selectedPoint?.scheduleType).toUpperCase() ===
                  "WINDOW"
                    ? "Intervalo horário"
                    : "Hora fixa"}
                </span>
              </button>
            </div>
          </div>


          <div className="loadevo-session-schedule-limits">
            <div>
              <span>Paragem anterior</span>
              <strong>
                {previousStop
                  ? `${pointName(previousStop)} · ${formatSchedule(previousStop)}`
                  : "Início da rota"}
              </strong>
            </div>

            <div>
              <span>Paragem seguinte</span>
              <strong>
                {nextStop
                  ? `${pointName(nextStop)} · ${formatSchedule(nextStop)}`
                  : "Fim da rota"}
              </strong>
            </div>
          </div>

          <p className="loadevo-session-conflict-hint">
            A validação respeita horas fixas e intervalos.
            Ainda não contabiliza tempos de condução,
            operações de carga/descarga ou diferenças de fuso horário.
          </p>
        </div>

        {(error || data.error) && (
          <p className="loadevo-session-conflict-error" role="alert">
            {error || data.error}
          </p>
        )}

        <div className="loadevo-session-conflict-actions">
          <button type="button" disabled={saving} onClick={onCancel}>
            Reverter movimento
          </button>

          <button
            type="button"
            className="is-primary"
            disabled={saving || isLocked(selectedPoint)}
            onClick={handleApply}
          >
            {saving ? "A guardar..." : "Guardar horário e ordem"}
          </button>
        </div>
      </div>

      {calendarPopup && selectedPoint && (
        <MapConflictCalendar
          key={`${calendarPopup.pointId}-${selectedPoint.date || ""}`}
          value={String(selectedPoint.date || "").slice(0, 10)}
          position={calendarPopup}
          onClose={() => setCalendarPopup(null)}
          onSelect={(newDate) => {
            updatePoint({ date: newDate });
            setCalendarPopup(null);
          }}
        />
      )}

      {schedulePopup && selectedPoint && (
        <MapConflictTimePicker
          key={`${schedulePopup.pointId}-${editIndex}`}
          kind={
            selectedPoint.type === "DELIVERY"
              ? "delivery"
              : "pickup"
          }
          initialType={selectedPoint.scheduleType || "FIXED"}
          initialStart={selectedPoint.hour}
          initialEnd={selectedPoint.hourEnd}
          position={schedulePopup}
          onClose={() => setSchedulePopup(null)}
          onApply={({ scheduleType, hour, hourEnd }) => {
            updatePoint({
              scheduleType,
              hour,
              hourEnd,
            });

            setSchedulePopup(null);
          }}
        />
      )}
    </div>,
    document.body
  );
}